import { useState, useRef, useCallback } from 'react';
import { colors } from '@sweep-io/sweep-design';

interface UseVerticalResizeProps {
  initialHeight?: number;
  minHeight?: number;
}

export function useVerticalResize({
  initialHeight = 400,
  minHeight = 200,
}: UseVerticalResizeProps = {}) {
  const [height, setHeight] = useState(initialHeight);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const startY = e.clientY;
    const startHeight = height;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const newHeight = Math.max(minHeight, startHeight + deltaY);
      setHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [height, minHeight]);

  const ResizeHandle = (
    <div
      onMouseDown={handleMouseDown}
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '8px',
        backgroundColor: isDragging ? colors.blue[300] : 'transparent',
        cursor: 'ns-resize',
        zIndex: isDragging ? 1000 : 1,
        borderRadius: '0 0 8px 8px',
      }}
      onMouseEnter={(e) => {
        if (!isDragging) {
          e.currentTarget.style.backgroundColor = colors.blue[300];
        }
      }}
      onMouseLeave={(e) => {
        if (!isDragging) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
    />
  );

  return {
    height,
    isDragging,
    containerRef,
    ResizeHandle,
  };
}
